import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { isStepConflict } from '@/lib/stepConflict';
import { Button } from '@ai-tutor/ui';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  Maximize,
  Settings,
  Subtitles,
  HelpCircle,
} from 'lucide-react';
import { lessonApi } from '@/lib/api-client';
import { VideoSkeleton } from '../skeletons/PanelSkeletons';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { type TranscriptItem } from '@/data/fixtures';
import { DoubtSession } from '@/components/features/learning/doubt/DoubtSession';
import { TranscriptList } from '@/components/features/learning/video/TranscriptList';
import { VideoDoubtsList } from '@/components/features/learning/video/VideoDoubtsList';
import { DoubtReplay } from '@/components/features/learning/video/DoubtReplay';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { isFeatureEnabled } from '@/config/app';
import { queryKeys } from '@/config/query-client';

function getCurrentTranscriptItem(
  currentTime: number,
  transcriptData: TranscriptItem[]
) {
  return transcriptData.find(
    item =>
      currentTime >= item.startTime &&
      (item.endTime ? currentTime < item.endTime : true)
  );
}

interface VideoRendererProps {
  onProgressUpdate?: (mediaType: string, progress: number) => void;
  lessonId?: string;
  hideControls?: boolean;
  hideHeading?: boolean;
}

export function VideoRenderer({
  onProgressUpdate,
  lessonId: propLessonId,
  hideControls = false,
  hideHeading = false,
}: VideoRendererProps) {
  const { id: paramLessonId } = useParams<{ id: string }>();
  const lessonId = propLessonId || paramLessonId;

  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);

  // Activity tracking gated on actual playback so ContentActivity.duration_seconds
  // reflects real playback time, not idle tab-open time. The hook starts a
  // ContentActivity row when `enabled` flips to true (play) and closes it on
  // false (pause / unmount).
  useActivityTracking({
    contentType: 'video',
    lessonId,
    enabled: isPlaying,
  });
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Doubt mode state
  const [isDoubtMode, setIsDoubtMode] = useState(false);
  const [currentScene, setCurrentScene] = useState<any>(null);

  // Replay mode - id of the recorded doubt currently playing back in the
  // video area. Mutually exclusive with live doubt mode; clicking a
  // doubt in the list pauses the video and swaps in DoubtReplay.
  const [replaySessionId, setReplaySessionId] = useState<string | null>(null);
  // Lifted play/pause state so the doubt-list cards can act as
  // play/pause controls in addition to the in-overlay control bar.
  // DoubtReplay is the source of truth for the audio element; it
  // notifies us via `onPlayingChange` and obeys `playing` when we
  // toggle it from the card.
  const [replayPlaying, setReplayPlaying] = useState(false);

  // Toggle between Transcript and Doubts panels below the video
  const [activeBelowTab, setActiveBelowTab] = useState<'transcript' | 'doubts'>(
    'transcript'
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const queryClient = useQueryClient();

  // Poll video endpoint. NOTE: status='pending' from this endpoint means "no
  // LessonVideo record exists yet" - i.e. the user has not requested generation.
  // Only 'processing' indicates an actual in-flight task, so we poll only then.
  const {
    data: videoData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.lessons.video(lessonId || ''),
    queryFn: () => lessonApi.getLessonVideo(lessonId!),
    refetchInterval: query =>
      query.state.data?.status === 'processing' ? 3000 : false,
    staleTime: 0,
    retry: 3,
    enabled: !!lessonId,
  });

  // Fetch lesson metadata for title
  const { data: lessonData } = useQuery({
    queryKey: queryKeys.lessons.metadata(lessonId || ''),
    queryFn: () => lessonApi.getLesson(lessonId!),
    staleTime: 60000, // Cache for 1 minute
    enabled: !!lessonId,
  });

  const handleRetry = async () => {
    const videoKey = queryKeys.lessons.video(lessonId || '');
    // Optimistically flip to "processing" so the panel updates the instant the
    // button is clicked, instead of waiting for the post-retry refetch (which
    // can still read the old 'failed' status before the backend flips it).
    const prev = queryClient.getQueryData(videoKey);
    if (prev && typeof prev === 'object') {
      await queryClient.cancelQueries({ queryKey: videoKey });
      queryClient.setQueryData(videoKey, {
        ...(prev as Record<string, unknown>),
        status: 'processing',
        error: null,
      });
    }
    try {
      await lessonApi.retryStep(lessonId ?? '', 'video');
      notify.success('Retrying video generation...');
      refetch();
    } catch (err: any) {
      // Roll back the optimistic flip - the retry never queued.
      if (prev) queryClient.setQueryData(videoKey, prev);
      // 409 = nothing to retry (already generated): show the real state.
      if (isStepConflict(err)) {
        notify.info(err.detail);
        refetch();
        return;
      }
      console.error('Failed to retry video generation:', err);
      notify.error(err.message || 'Failed to retry video generation');
    }
  };

  const handleProgressChange = (newProgress: number) => {
    setProgress(newProgress);
    onProgressUpdate?.('video', newProgress);
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const handleSpeedChange = (speed: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackRate(speed);
      setShowSpeedMenu(false);
    }
  };

  const handleFullscreen = () => {
    if (videoContainerRef.current) {
      if (!isFullscreen) {
        if (videoContainerRef.current.requestFullscreen) {
          videoContainerRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    }
  };

  const toggleCaptions = () => {
    // The styled React overlay below the <video> is the captions UI.
    // Keep the native track permanently disabled so we don't render
    // both caption layers simultaneously.
    if (videoRef.current) {
      const tracks = videoRef.current.textTracks;
      if (tracks.length > 0) {
        tracks[0].mode = 'disabled';
      }
    }
    setCaptionsEnabled(prev => !prev);
  };

  const handleSeek = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Auto-hide controls
  const showControlsAndStartTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Early return after all hooks
  if (!lessonId) {
    return <div className='text-red-500'>No lesson ID provided</div>;
  }

  // Doubt mode handlers
  const handleAskDoubt = async () => {
    if (!videoRef.current) {
      notify.error('Video player not ready');
      return;
    }

    // COMMENTED OUT: Metadata/scene validation - we'll pass null for currentScene
    // if (!videoData?.metadata_url) {
    //   notify.error('Video metadata not available yet. Please wait for the video to finish generating.')
    //   return
    // }

    // Pause video
    videoRef.current.pause();
    setIsPlaying(false);

    // COMMENTED OUT: Scene fetching logic - backend will handle context internally
    // // Fetch metadata if not already loaded
    // if (metadataScenes.length === 0) {
    //   try {
    //     console.log('Fetching metadata from:', videoData.metadata_url)
    //     const response = await fetch(videoData.metadata_url)

    //     if (!response.ok) {
    //       throw new Error(`Failed to fetch metadata: ${response.status}`)
    //     }

    //     const metadata = await response.json()
    //     console.log('Metadata loaded:', metadata)

    //     const scenes = metadata.scenes || []
    //     if (scenes.length === 0) {
    //       notify.error('No scenes found in video metadata')
    //       return
    //     }

    //     setMetadataScenes(scenes)

    //     // Determine current scene
    //     console.log(`Finding scene at time ${currentTime}`)
    //     const scene = scenes.find(
    //       (s: any) => currentTime >= s.start_time && currentTime < s.end_time
    //     )

    //     if (!scene) {
    //       // If no exact match, use the closest scene
    //       const closestScene = scenes.reduce((prev: any, curr: any) => {
    //         const prevDiff = Math.abs(prev.start_time - currentTime)
    //         const currDiff = Math.abs(curr.start_time - currentTime)
    //         return currDiff < prevDiff ? curr : prev
    //       })

    //       console.log('No exact scene match, using closest scene:', closestScene)
    //       setCurrentScene(closestScene)
    //       notify.info('Using closest scene for your doubt', { icon: 'ℹ️' })
    //     } else {
    //       console.log('Found scene:', scene)
    //       setCurrentScene(scene)
    //     }
    //   } catch (err: any) {
    //     console.error('Failed to fetch metadata:', err)
    //     notify.error(`Could not load lesson metadata: ${err.message}`)
    //     return
    //   }
    // } else {
    //   // Use already loaded metadata
    //   console.log(`Finding scene at time ${currentTime} from cached metadata`)
    //   const scene = metadataScenes.find(
    //     (s: any) => currentTime >= s.start_time && currentTime < s.end_time
    //   )

    //   if (!scene) {
    //     // If no exact match, use the closest scene
    //     const closestScene = metadataScenes.reduce((prev: any, curr: any) => {
    //       const prevDiff = Math.abs(prev.start_time - currentTime)
    //       const currDiff = Math.abs(curr.start_time - currentTime)
    //       return currDiff < prevDiff ? curr : prev
    //     })

    //     console.log('No exact scene match, using closest scene:', closestScene)
    //     setCurrentScene(closestScene)
    //     notify.info('Using closest scene for your doubt', { icon: 'ℹ️' })
    //   } else {
    //     console.log('Found scene:', scene)
    //     setCurrentScene(scene)
    //   }
    // }

    // Set a dummy scene object so the DoubtSession renders
    setCurrentScene({ timestamp: currentTime });

    // Enter doubt mode (no browser PiP needed with containerized approach)
    setIsDoubtMode(true);
  };

  const handleExitDoubt = () => {
    setIsDoubtMode(false);
    setCurrentScene(null);
  };

  const handleReplayDoubt = (sessionId: string) => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    // Replay and live doubt mode are mutually exclusive - close any
    // active live session first so we don't end up with two overlays.
    setIsDoubtMode(false);
    setCurrentScene(null);
    // Switching to a (potentially different) session - request play.
    // DoubtReplay's audio element will fire `onPlayingChange(true)`
    // once playback actually starts; if the browser blocks autoplay,
    // it'll fire `onPlayingChange(false)` on the failed attempt.
    setReplaySessionId(sessionId);
    setReplayPlaying(true);
  };

  const handleExitReplay = () => {
    setReplaySessionId(null);
    setReplayPlaying(false);
  };

  // Toggle play/pause on the active replay. Called from the doubt
  // list card so the user can pause without leaving the list.
  const handleToggleReplayPlay = () => {
    setReplayPlaying(p => !p);
  };

  // Initial fetch - ArtifactGate already confirmed
  // `pipelineStatus.video === 'completed'` before mounting us, so this
  // is a GET round-trip rather than a pipeline run. Fetch-mode copy
  // ("Loading Video" / "Fetching your video.") keeps the message honest.
  if (isLoading || !videoData) {
    return <VideoSkeleton mode='fetching' />;
  }

  // `pending` / `processing` are race-condition fallbacks - StepGate
  // above already filters on `pipelineStatus.video === 'completed'`
  // before mounting us, so we should never see in-flight artifact
  // statuses. If we do (cache lag, etc.), show the skeleton instead of
  // a broken player.
  if (videoData.status === 'pending' || videoData.status === 'processing') {
    return <VideoSkeleton />;
  }

  // Failed state
  if (videoData.status === 'failed' || error) {
    return (
      <PanelFallback
        variant='step'
        title='Video generation failed'
        description={
          videoData?.error?.message ||
          "We couldn't generate the video lesson. Retry, or reload the page if it keeps failing."
        }
        primary={{ label: 'Retry', onClick: handleRetry }}
        secondary={{
          label: 'Reload page',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  // Completed state - Show full video player
  if (videoData.status === 'completed') {
    // Use actual video URL from backend
    const videoUrl = videoData.video_url;

    // Scene-level transcript from backend metadata.json. Caption overlay
    // still uses the legacy `TranscriptItem` shape (start/end seconds).
    const scenes = videoData.scenes ?? [];
    const transcriptData: TranscriptItem[] = scenes.map(s => {
      const m = Math.floor(s.start_time / 60);
      const ss = Math.floor(s.start_time % 60);
      return {
        id: s.scene_id,
        startTime: s.start_time,
        endTime: s.end_time,
        timestamp: `${m}:${ss.toString().padStart(2, '0')}`,
        text: s.narration,
      };
    });
    const currentCaption = getCurrentTranscriptItem(
      currentTime,
      transcriptData
    );

    return (
      <>
        <div className='flex h-full flex-col gap-6 overflow-y-auto p-4'>
          {/* Lesson Heading - match the prose h1 from the text panel */}
          {!hideHeading && lessonData && (
            <h1 className='text-2xl font-semibold text-foreground'>
              {lessonData.title}
            </h1>
          )}
          {/* Video Player Container with Layered Doubt Canvas */}
          <div className='flex justify-start'>
            <div className='w-full max-w-4xl'>
              {/* Fullscreen Container - wraps both video and canvas for fullscreen support */}
              <div
                ref={videoContainerRef}
                className={`relative aspect-video w-full ${isFullscreen ? 'fullscreen-video-container' : ''}`}
              >
                {/* Layer 1a: Live doubt session (mic + canvas) */}
                {isDoubtMode && currentScene && (
                  <div className='absolute inset-0 z-10'>
                    <div className='h-full w-full overflow-hidden rounded-lg bg-card shadow-lg'>
                      <DoubtSession
                        lessonId={lessonId}
                        currentTime={currentTime}
                        onExit={handleExitDoubt}
                      />
                    </div>
                  </div>
                )}

                {/* Layer 1b: Recorded doubt replay (audio + canvas) */}
                {replaySessionId && lessonId && (
                  <div className='absolute inset-0 z-10'>
                    <div className='h-full w-full overflow-hidden rounded-lg bg-card shadow-lg'>
                      <DoubtReplay
                        lessonId={lessonId}
                        sessionId={replaySessionId}
                        onExit={handleExitReplay}
                        playing={replayPlaying}
                        onPlayingChange={setReplayPlaying}
                      />
                    </div>
                  </div>
                )}

                {/* Layer 2: Video Player. Mounted only when NOT in replay
                   mode - replay takes over the area entirely (no need for
                   the underlying video to keep buffering or render). */}
                {!replaySessionId && (
                  <div
                    className={`overflow-hidden rounded-lg bg-black transition-all duration-300 ${
                      !showControls && isPlaying
                        ? 'cursor-none'
                        : 'cursor-pointer'
                    } ${
                      isDoubtMode && currentScene
                        ? `absolute bottom-4 left-4 z-20 cursor-pointer shadow-2xl hover:scale-105 ${
                            isFullscreen ? 'h-54 w-96' : 'h-36 w-64'
                          }`
                        : 'absolute inset-0 z-10'
                    }`}
                    onMouseMove={showControlsAndStartTimer}
                    onClick={
                      isDoubtMode && currentScene ? handleExitDoubt : undefined
                    }
                  >
                    <video
                      ref={videoRef}
                      className='h-full w-full object-cover'
                      onClick={togglePlayPause}
                      crossOrigin='anonymous'
                      onTimeUpdate={() => {
                        if (videoRef.current) {
                          setCurrentTime(videoRef.current.currentTime);
                          const prog =
                            (videoRef.current.currentTime /
                              videoRef.current.duration) *
                            100;
                          setProgress(prog);
                          handleProgressChange(prog);
                        }
                      }}
                      onLoadedMetadata={() => {
                        if (videoRef.current) {
                          setDuration(videoRef.current.duration);
                        }
                      }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    >
                      <source src={videoUrl} type='video/mp4' />
                      {videoData.subtitles_url && (
                        <track
                          kind='subtitles'
                          src={videoData.subtitles_url}
                          srcLang='en'
                          label='English'
                        />
                      )}
                      Your browser does not support the video tag.
                    </video>

                    {/* Netflix-style Captions */}
                    {currentCaption && captionsEnabled && (
                      <div
                        className={`absolute left-1/2 z-30 -translate-x-1/2 transform transition-all duration-300 ${
                          showControls
                            ? isFullscreen
                              ? 'bottom-24'
                              : 'bottom-20'
                            : isFullscreen
                              ? 'bottom-12'
                              : 'bottom-8'
                        }`}
                        style={{
                          maxWidth: '90%',
                          width: 'fit-content',
                        }}
                      >
                        <div
                          className='rounded-sm bg-black/30 px-3 py-2 text-center font-medium leading-tight text-white'
                          style={{
                            fontSize: isFullscreen ? '20px' : '16px',
                            lineHeight: '1.4',
                            fontFamily: 'inherit',
                          }}
                        >
                          {currentCaption.text}
                        </div>
                      </div>
                    )}

                    {/* Center Play/Pause Button (when controls are hidden) */}
                    {hideControls && (
                      <div
                        className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                          showControls ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <div
                          onClick={togglePlayPause}
                          className='group pointer-events-auto relative transform cursor-pointer transition-all duration-300 ease-out hover:scale-110 active:scale-95'
                          onMouseMove={showControlsAndStartTimer}
                        >
                          {/* Outer ring with subtle pulse animation */}
                          <div className='absolute inset-0 h-20 w-20 animate-pulse rounded-full border-2 border-white/30'></div>

                          {/* Main button */}
                          <div className='relative flex h-20 w-20 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white shadow-2xl backdrop-blur-sm transition-all duration-300 ease-out hover:bg-black/80'>
                            {/* Glass effect overlay */}
                            <div className='pointer-events-none absolute inset-1 rounded-full bg-gradient-to-tr from-white/10 to-transparent'></div>

                            {/* Icon */}
                            <div className='relative z-10'>
                              {isPlaying ? (
                                <Pause className='h-8 w-8 drop-shadow-lg' />
                              ) : (
                                <Play className='ml-0.5 h-8 w-8 drop-shadow-lg' />
                              )}
                            </div>
                          </div>

                          {/* Hover ring */}
                          <div className='absolute inset-0 h-20 w-20 scale-110 rounded-full border-2 border-white/50 opacity-0 transition-all duration-300 ease-out group-hover:opacity-100'></div>
                        </div>
                      </div>
                    )}

                    {/* Video Controls Overlay */}
                    {!hideControls && (
                      <div
                        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
                          showControls
                            ? 'opacity-100'
                            : 'pointer-events-none opacity-0'
                        }`}
                        onMouseMove={showControlsAndStartTimer}
                      >
                        <div className='space-y-2'>
                          {/* Progress Bar */}
                          <div className='flex items-center gap-2'>
                            <span className='min-w-[40px] text-xs text-white/75'>
                              {formatTime(currentTime)}
                            </span>
                            <div
                              className='h-1 flex-1 cursor-pointer overflow-hidden rounded-full bg-white/30'
                              onClick={e => {
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const pos =
                                  (e.clientX - rect.left) / rect.width;
                                handleSeek(pos * duration);
                              }}
                            >
                              <div
                                className='h-full bg-blue-500 transition-all duration-100 dark:bg-primary'
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className='min-w-[40px] text-xs text-white/75'>
                              {formatTime(duration)}
                            </span>
                          </div>

                          {/* Control Buttons */}
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={togglePlayPause}
                                className='p-2 text-white hover:bg-white/20'
                              >
                                {isPlaying ? (
                                  <Pause className='h-4 w-4' />
                                ) : (
                                  <Play className='h-4 w-4' />
                                )}
                              </Button>

                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={() => handleSeek(currentTime - 10)}
                                className='p-2 text-white hover:bg-white/20'
                              >
                                <SkipBack className='h-4 w-4' />
                              </Button>

                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={() => handleSeek(currentTime + 10)}
                                className='p-2 text-white hover:bg-white/20'
                              >
                                <SkipForward className='h-4 w-4' />
                              </Button>

                              <div className='flex items-center gap-2'>
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  onClick={toggleMute}
                                  className='p-2 text-white hover:bg-white/20'
                                >
                                  {isMuted ? (
                                    <VolumeX className='h-4 w-4' />
                                  ) : (
                                    <Volume2 className='h-4 w-4' />
                                  )}
                                </Button>
                                <input
                                  type='range'
                                  min='0'
                                  max='1'
                                  step='0.1'
                                  value={volume}
                                  onChange={e =>
                                    handleVolumeChange(
                                      parseFloat(e.target.value)
                                    )
                                  }
                                  className='h-1 w-16 cursor-pointer appearance-none rounded-full bg-white/30'
                                />
                              </div>
                            </div>

                            <div className='flex items-center gap-2'>
                              {/* Doubt mode is a sub-feature of video; gated
                                  by the same flag. Redundant with the hidden
                                  Video tab, but guards against the renderer
                                  ever being mounted elsewhere. */}
                              {isFeatureEnabled('video') && (
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  onClick={handleAskDoubt}
                                  className='flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-brand-700 hover:shadow-xl dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary'
                                  title='Ask Doubt - Get instant help'
                                >
                                  <HelpCircle className='h-4 w-4' />
                                  <span>Ask Doubt</span>
                                </Button>
                              )}

                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={toggleCaptions}
                                className={`p-2 text-white hover:bg-white/20 ${
                                  captionsEnabled ? 'bg-white/10' : ''
                                }`}
                              >
                                <Subtitles className='h-4 w-4' />
                              </Button>

                              <div className='relative'>
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  onClick={() =>
                                    setShowSpeedMenu(!showSpeedMenu)
                                  }
                                  className='p-2 text-white hover:bg-white/20'
                                >
                                  <Settings className='h-4 w-4' />
                                </Button>
                                {showSpeedMenu && (
                                  <div className='absolute bottom-full right-0 mb-2 min-w-[80px] rounded-lg bg-black/90 p-2'>
                                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                                      <button
                                        key={speed}
                                        onClick={() => handleSpeedChange(speed)}
                                        className={`block w-full rounded-sm px-2 py-1 text-left text-sm text-white hover:bg-white/20 ${
                                          playbackRate === speed
                                            ? 'bg-blue-600'
                                            : ''
                                        }`}
                                      >
                                        {speed}x
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={handleFullscreen}
                                className='p-2 text-white hover:bg-white/20'
                              >
                                <Maximize className='h-4 w-4' />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>{' '}
              {/* Close layered container */}
            </div>{' '}
            {/* Close max-w-4xl */}
          </div>{' '}
          {/* Close video container */}
          {/* Transcript / Doubts toggle */}
          <div className='w-full max-w-4xl'>
            <div
              role='tablist'
              aria-label='Video supplementary content'
              className='mb-3 inline-flex rounded-lg bg-muted p-1'
            >
              {(['transcript', 'doubts'] as const).map(tab => {
                const isActive = activeBelowTab === tab;
                return (
                  <button
                    key={tab}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    onClick={() => setActiveBelowTab(tab)}
                    className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                      isActive
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
            <div className='h-96 overflow-y-auto rounded-lg border border-border'>
              {activeBelowTab === 'transcript' ? (
                <TranscriptList
                  scenes={scenes}
                  currentTime={currentTime}
                  onSeek={handleSeek}
                />
              ) : lessonId ? (
                <VideoDoubtsList
                  lessonId={lessonId}
                  onReplay={handleReplayDoubt}
                  activeSessionId={replaySessionId}
                  activeIsPlaying={replayPlaying}
                  onTogglePlay={handleToggleReplayPlay}
                  onDeleted={(deletedId: string) => {
                    if (replaySessionId === deletedId) handleExitReplay();
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>{' '}
        {/* Close main container */}
      </>
    );
  }

  // Default fallback
  return (
    <div className='py-8 text-center text-muted-foreground'>
      No video available
    </div>
  );
}
